<template>
  <div class="flex flex-col gap-4 text-left" data-testid="check-attempts-panel">
    <!-- Filtros -->
    <div class="flex flex-wrap items-end gap-3">
      <template v-if="isUnit">
        <div class="flex items-end gap-2" data-testid="filter-period">
          <v-text-field
            v-model="state.filters.value.startDate"
            type="date"
            label="De"
            density="compact"
            variant="outlined"
            hide-details
            class="w-44"
          />
          <v-text-field
            v-model="state.filters.value.endDate"
            type="date"
            label="Até"
            density="compact"
            variant="outlined"
            hide-details
            class="w-44"
          />
        </div>
        <v-autocomplete
          v-model="state.filters.value.doctorId"
          :items="state.doctors.value"
          item-title="name"
          item-value="id"
          label="Médico"
          density="compact"
          variant="outlined"
          clearable
          hide-details
          class="min-w-[220px]"
          data-testid="filter-doctor"
        />
      </template>
      <v-select
        v-model="state.filters.value.type"
        :items="typeOptions"
        label="Tipo"
        density="compact"
        variant="outlined"
        clearable
        hide-details
        class="w-36"
        data-testid="filter-type"
      />
      <v-select
        v-model="state.filters.value.outcome"
        :items="outcomeOptions"
        label="Resultado"
        density="compact"
        variant="outlined"
        clearable
        hide-details
        class="w-40"
        data-testid="filter-outcome"
      />
      <v-select
        v-model="state.filters.value.reason"
        :items="reasonOptions"
        label="Motivo"
        density="compact"
        variant="outlined"
        clearable
        hide-details
        class="min-w-[200px]"
        data-testid="filter-reason"
      />
      <v-btn
        color="primary"
        variant="flat"
        :loading="state.isLoading.value"
        data-testid="filter-apply"
        @click="apply"
      >
        Aplicar
      </v-btn>
      <v-btn variant="text" data-testid="filter-reset" @click="reset">Limpar</v-btn>
      <v-btn
        variant="outlined"
        color="primary"
        :loading="isExporting"
        :disabled="state.isLoading.value"
        data-testid="filter-export"
        @click="exportXlsx"
      >
        <excelIcon width="18" height="18" color="currentColor" class="mr-2" />
        Exportar Excel
      </v-btn>
    </div>

    <p v-if="rangeError" class="text-sm text-red-600" data-testid="filter-range-error">
      {{ rangeError }}
    </p>
    <p v-if="state.error.value" class="text-sm text-red-600" data-testid="check-attempts-error">
      {{ state.error.value }}
    </p>

    <!-- KPIs -->
    <div v-if="state.summary.value" class="grid grid-cols-3 gap-3">
      <div
        class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
        data-testid="check-attempts-kpi-total"
      >
        <p class="text-lg font-bold">{{ state.summary.value.total }}</p>
        <p class="text-[11px] uppercase tracking-wide text-gray-500">Tentativas</p>
      </div>
      <div
        class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
        data-testid="check-attempts-kpi-rejected"
      >
        <p class="text-lg font-bold text-red-600">{{ state.summary.value.rejected }}</p>
        <p class="text-[11px] uppercase tracking-wide text-gray-500">Rejeitadas</p>
      </div>
      <div
        class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
        data-testid="check-attempts-kpi-max-distance"
      >
        <p class="text-lg font-bold">
          {{ formatDistance(state.summary.value.maxRejectedDistanceM) }}
        </p>
        <p class="text-[11px] uppercase tracking-wide text-gray-500">Maior distância rejeitada</p>
      </div>
    </div>

    <!-- Mapa -->
    <div
      v-if="mapCenter"
      :class="compact ? 'h-56' : 'h-80'"
      class="w-full overflow-hidden rounded-xl border border-gray-100"
      data-testid="check-attempts-map"
    >
      <GoogleMaps
        :latitude="mapCenter.lat"
        :longitude="mapCenter.lng"
        :radius="state.healthUnit.value?.reachRadiusM ?? undefined"
        :draggable="false"
        :show-info="false"
        :points="state.points.value"
        :selected-point-id="state.selectedId.value ?? undefined"
        @point-click="state.select"
      />
    </div>
    <div
      v-else
      class="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-400"
      data-testid="check-attempts-map-empty"
    >
      Mapa indisponível (unidade sem coordenadas ou SDK não carregado).
    </div>
    <div class="flex gap-4 text-xs text-gray-600">
      <span
        ><i
          class="inline-block h-2.5 w-2.5 rounded-full align-middle"
          :style="{ background: ACCEPTED_COLOR }"
        />
        aceita</span
      >
      <span
        ><i
          class="inline-block h-2.5 w-2.5 rounded-full align-middle"
          :style="{ background: REJECTED_COLOR }"
        />
        rejeitada</span
      >
      <span
        ><i class="inline-block h-2.5 w-2.5 rounded-full align-middle bg-blue-500" /> raio atual da
        unidade</span
      >
    </div>

    <!-- Tabela -->
    <div
      v-if="!state.isLoading.value && state.items.value.length === 0"
      class="rounded-lg border border-gray-100 p-4 text-sm text-gray-500"
      data-testid="check-attempts-empty"
    >
      Nenhuma tentativa registrada no período. Tentativas em que o app não conseguiu obter a
      localização (permissão negada ou GPS indisponível) ainda não são enviadas ao servidor.
    </div>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-[11px] uppercase tracking-wide text-gray-500">
            <th class="py-2 pr-3">Data/hora</th>
            <th v-if="isUnit" class="py-2 pr-3">Médico</th>
            <th v-if="isUnit" class="py-2 pr-3">Plantão</th>
            <th class="py-2 pr-3">Tipo</th>
            <th class="py-2 pr-3">Resultado</th>
            <th class="py-2 pr-3">Distância</th>
            <th class="py-2 pr-3">Raio</th>
            <th class="py-2 pr-3">Pino na hora</th>
            <th class="py-2 pr-3">Coordenadas</th>
            <th class="py-2 pr-3">App</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in state.items.value"
            :key="item.id"
            class="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
            :class="{ 'is-selected bg-primary/5': state.selectedId.value === item.id }"
            :data-testid="`check-attempts-row-${item.id}`"
            @click="state.select(item.id)"
          >
            <td class="py-2 pr-3 whitespace-nowrap">{{ formatDateTime(item.createdAt) }}</td>
            <td v-if="isUnit" class="py-2 pr-3">{{ item.doctor?.name ?? '—' }}</td>
            <td v-if="isUnit" class="py-2 pr-3 whitespace-nowrap">
              <template v-if="item.station"
                >#{{ item.station.id }} · {{ formatStation(item.station) }}</template
              >
              <template v-else>—</template>
            </td>
            <td class="py-2 pr-3">{{ CHECK_ATTEMPT_TYPE_LABELS[item.type] }}</td>
            <td class="py-2 pr-3 whitespace-nowrap">
              <!-- `whitespace-nowrap` + `truncate`: rótulos longos ("Permissão de
                   localização negada") quebravam a pílula em duas linhas. O
                   `max-w` é rede de segurança — o tooltip (`title`) continua
                   entregando a mensagem inteira. -->
              <span
                class="inline-block max-w-[220px] truncate whitespace-nowrap align-middle rounded-full px-2 py-0.5 text-[11px] font-semibold"
                data-testid="check-attempts-result-pill"
                :class="
                  item.outcome === 'ACCEPTED'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                "
                :title="item.message ?? ''"
              >
                {{ reasonLabel(item) }}
              </span>
            </td>
            <td class="py-2 pr-3 whitespace-nowrap">{{ formatDistance(item.distanceM) }}</td>
            <td class="py-2 pr-3 whitespace-nowrap">{{ formatDistance(item.reachRadiusM) }}</td>
            <td class="py-2 pr-3 whitespace-nowrap">
              {{ formatCoords(item.unitLatitude, item.unitLongitude) }}
              <v-icon
                v-if="item.unitPinDiffersFromCurrent"
                :icon="mdiAlertOutline"
                size="16"
                class="text-amber-600"
                title="Pino da unidade era diferente do atual"
                data-testid="pin-differs"
              />
            </td>
            <td class="py-2 pr-3 whitespace-nowrap">
              <a
                v-if="item.checkLatitude != null && item.checkLongitude != null"
                :href="mapsLink(item.checkLatitude, item.checkLongitude)"
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary30 hover:underline"
                @click.stop
              >
                {{ formatCoords(item.checkLatitude, item.checkLongitude) }}
              </a>
              <span v-else>—</span>
            </td>
            <td class="py-2 pr-3 max-w-[160px] truncate" :title="item.userAgent ?? ''">
              {{ item.userAgent ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="state.pageCount.value > 1" class="flex justify-center">
      <v-pagination
        v-model="state.page.value"
        :length="state.pageCount.value"
        :total-visible="5"
        density="comfortable"
        @update:model-value="load"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { mdiAlertOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import GoogleMaps from '@/components/GoogleMaps.vue'
import {
  CHECK_ATTEMPT_REASON_LABELS,
  CHECK_ATTEMPT_TYPE_LABELS
} from '@/Network/Types/Responses/CheckAttemptsResponses'
import {
  useCheckAttempts,
  isHealthUnitSource,
  MAX_RANGE_DAYS,
  type CheckAttemptsSource,
  type CheckAttemptsUiFilters
} from './useCheckAttempts'
import {
  ACCEPTED_COLOR,
  REJECTED_COLOR,
  formatCoords,
  formatDistance,
  mapsLink,
  reasonLabel
} from './checkAttemptsFormat'

const props = withDefaults(
  defineProps<{
    source: CheckAttemptsSource
    compact?: boolean
    initialFilters?: Partial<CheckAttemptsUiFilters>
  }>(),
  { compact: false, initialFilters: undefined }
)
const emit = defineEmits<{ (e: 'update:filters', filters: CheckAttemptsUiFilters): void }>()

const excelIcon = defineAsyncComponent(() => import('@/assets/icons/excel.vue'))
const toast = useToast()
const { notifyError } = useErrorToast()

const state = useCheckAttempts(props.source, props.initialFilters)
const isUnit = computed(() => isHealthUnitSource(props.source))
const rangeError = ref<string | null>(null)
const isExporting = ref(false)

const typeOptions = Object.entries(CHECK_ATTEMPT_TYPE_LABELS).map(([value, title]) => ({
  value,
  title
}))
const outcomeOptions = [
  { value: 'ACCEPTED', title: 'Aceita' },
  { value: 'REJECTED', title: 'Rejeitada' }
]
const reasonOptions = Object.entries(CHECK_ATTEMPT_REASON_LABELS).map(([value, title]) => ({
  value,
  title
}))

const isSdkReady = computed(
  () =>
    typeof window !== 'undefined' &&
    !!(window as unknown as { google?: { maps?: unknown } }).google?.maps
)
const mapCenter = computed(() => {
  const hu = state.healthUnit.value
  if (!isSdkReady.value || !hu || hu.latitude == null || hu.longitude == null) return null
  return { lat: hu.latitude, lng: hu.longitude }
})

function validateRange(): boolean {
  rangeError.value = null
  if (!isUnit.value) return true
  const { startDate, endDate } = state.filters.value
  const days = (Date.parse(endDate) - Date.parse(startDate)) / 86_400_000
  if (Number.isNaN(days) || days < 0) rangeError.value = 'Período inválido'
  else if (days > MAX_RANGE_DAYS) rangeError.value = `Período máximo de ${MAX_RANGE_DAYS} dias`
  return rangeError.value === null
}

async function load(): Promise<void> {
  if (!validateRange()) return
  await state.load()
  emit('update:filters', { ...state.filters.value })
}

async function apply(): Promise<void> {
  state.page.value = 1
  await load()
}

async function reset(): Promise<void> {
  state.resetFilters()
  await load()
}

async function exportXlsx(): Promise<void> {
  if (!validateRange()) return
  isExporting.value = true
  try {
    const link = await state.exportXlsx()
    link.click()
    link.remove()
    toast.success('Planilha exportada com sucesso')
  } catch (e) {
    notifyError(e, 'Não foi possível exportar as tentativas de check')
  } finally {
    isExporting.value = false
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatStation(s: { date: string; startHour: string; endHour: string }): string {
  const hh = (iso: string) =>
    new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${new Date(s.date).toLocaleDateString('pt-BR')} ${hh(s.startHour)}–${hh(s.endHour)}`
}

watch(
  () => props.source,
  () => apply(),
  { deep: true }
)
onMounted(load)
</script>
